import { useAutoSave } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { useDataTableContext } from "@msuite/ui/table";
import { uniqueArray } from "@msuite/utils/array";
import type { SubstitutionPlanFrontendFormSchema } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import { useEffect } from "react";
import { Controller, useForm, useFormContext, useWatch } from "react-hook-form";
import { useSubstitutionPlanFormContext } from "./context";

/** Props Interface */
interface SubstitutionPlanQuickNoteProps {
	itemId: string;
}

export function SubstitutionPlanQuickNote({ itemId }: SubstitutionPlanQuickNoteProps) {
	/** Context */
	const methods = useFormContext<SubstitutionPlanFrontendFormSchema>();
	const { getSelectedRowIds, handleOnSetItemRenderId, itemRenderId, table } = useDataTableContext();
	const { getItemIndex } = useSubstitutionPlanFormContext();
	const index = getItemIndex(itemId);
	const innerMethods = useForm({
		defaultValues: {
			note: methods.getValues(`items.${index}.quickNote`),
		},
	});

	useAutoSave(innerMethods, (data) => {
		methods.setValue(`items.${index}.quickNote`, data.note, { shouldDirty: true });
	});

	/** Hooks */
	const { t } = useTranslation();
	const isNotRequired = useWatch({
		control: methods.control,
		name: `items.${index}.isAssignmentNotRequired`,
	});

	function handleOnBatchUpdate(note: string) {
		const ids = uniqueArray([itemId].concat(getSelectedRowIds()));
		for (const id of ids) {
			const index = getItemIndex(id);
			const path = `items.${index}.quickNote` as const;
			methods.setValue(path, note);
		}
		handleOnSetItemRenderId();
		table.resetRowSelection();
	}

	useEffect(() => {
		innerMethods.setValue("note", methods.getValues(`items.${index}.quickNote`));
	}, [itemRenderId]);

	if (isNotRequired) return guardString(null);

	/** Render */
	return (
		<Controller
			control={innerMethods.control}
			name="note"
			render={({ field }) => {
				return (
					<input
						className="inset-0 absolute px-3 focus:border border-primary-500 box-border"
						placeholder={t("notes.add_note")}
						disabled={isNotRequired}
						onChange={field.onChange}
						onBlur={() => {
							handleOnBatchUpdate(guardString(field.value, ""));
						}}
						value={`${field.value || ""}`}
					/>
				);
			}}
		/>
	);
}
